import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Crown, Calendar, CreditCard, AlertCircle, ExternalLink, RefreshCcw, X as XIcon, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import NavigationMenu from '../components/NavigationMenu';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const ManageSubscriptionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subResponse, historyResponse] = await Promise.all([
        api.get('/subscription/usage-status'),
        api.get('/subscription/payment-history')
      ]);
      
      setSubscriptionData(subResponse.data);
      setPaymentHistory(historyResponse.data.transactions || []);
      
      console.log('📊 Manage page - subscription data:', subResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await api.post('/subscription/create-portal-session', {
        return_url: window.location.href
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error(t('common.error'));
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelling(true);
      await api.post('/subscription/cancel');
      toast.success(t('subscription.cancelSubscription'));
      setShowCancelDialog(false);
      
      // REFRESH USER IMMEDIATELY - Updates AuthContext to Free status
      console.log('🔄 Refreshing user after cancellation...');
      await refreshUser();
      console.log('✅ User refreshed - Free status active');
      
      await fetchData();
    } catch (error) {
      console.error('Error cancelling:', error);
      toast.error(t('common.error'));
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    try {
      await api.post('/subscription/reactivate');
      toast.success(t('subscription.reactivate'));
      
      // REFRESH USER IMMEDIATELY - Updates AuthContext to Premium status
      console.log('🔄 Refreshing user after reactivation...');
      await refreshUser();
      console.log('✅ User refreshed - Premium status active');
      
      await fetchData();
    } catch (error) {
      console.error('Error reactivating:', error);
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <NavigationMenu />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  const { subscription, usage, limits } = subscriptionData || {};
  const isPremium = subscription?.plan === 'premium' || subscription?.plan === 'family';
  const isFamily = subscription?.plan === 'family';
  const isTrial = subscription?.status === 'trial';
  const isCancelled = subscription?.status === 'cancelled';

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <NavigationMenu />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-amber-900 mb-2">Gerenciar Assinatura</h1>
          <p className="text-gray-600">Gerencie sua assinatura e histórico de pagamentos</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Current Plan */}
          <Card className="lg:col-span-2 p-6 border-2 border-orange-200">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-amber-900 mb-2">Plano Atual</h2>
                <div className="flex items-center gap-2">
                  <Badge className={`text-lg px-4 py-1 ${
                    isFamily ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white' :
                    isPremium ? 'bg-gradient-to-r from-orange-600 to-amber-600 text-white' : 
                    'bg-gray-200 text-gray-700'
                  }`}>
                    {isFamily ? '👨‍👩‍👧‍👦 Familiar' : isPremium ? '👑 Premium' : '🆓 Gratuito'}
                  </Badge>
                  {isTrial && (
                    <Badge variant="secondary">Trial Ativo</Badge>
                  )}
                  {isCancelled && (
                    <Badge variant="destructive">Cancelada</Badge>
                  )}
                </div>
              </div>
              
              {isPremium && !isCancelled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCancelDialog(true)}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  Cancelar Assinatura
                </Button>
              )}
              
              {isCancelled && (
                <Button
                  onClick={handleReactivate}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Reativar
                </Button>
              )}
            </div>

            {/* Subscription Details */}
            <div className="space-y-4">
              {/* NOVO: Cancelado durante Trial */}
              {isCancelled && isTrial && subscription?.trial_ends_at && (
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-yellow-900 mb-2">
                        ⚠️ Plano Cancelado - Trial Ativo
                      </p>
                      <p className="text-sm text-yellow-800 mb-2">
                        Você ainda tem acesso <strong>Premium completo</strong> até o fim do trial:
                      </p>
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-yellow-700" />
                        <span className="font-bold text-yellow-900">
                          {new Date(subscription.trial_ends_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      <p className="text-xs text-yellow-700">
                        Após essa data, você retornará ao plano Gratuito. 
                        Não haverá cobrança, pois o trial foi cancelado antes do término.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Trial Ativo (não cancelado) */}
              {isTrial && !isCancelled && subscription?.trial_ends_at && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-orange-900 mb-1">Trial Ativo</p>
                      <p className="text-sm text-gray-700">
                        Seu trial termina em {new Date(subscription.trial_ends_at).toLocaleDateString('pt-BR')}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Após o trial, será cobrado R$ 4,99/mês automaticamente
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isPremium && subscription?.subscription_ends_at && !isTrial && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Próxima Cobrança</p>
                      <p className="text-sm text-gray-600">
                        {new Date(subscription.subscription_ends_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <p className="text-lg font-bold text-orange-600">R$ 4,99</p>
                </div>
              )}

              {!isPremium && usage && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-600 mb-1">Receitas este mês</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {usage.recipes_this_month}/{limits?.recipes_per_month || 5}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-600 mb-1">IA esta semana</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {usage.ai_generations_this_week}/{limits?.ai_generations_per_week || 3}
                    </p>
                  </div>
                </div>
              )}

              {isPremium && (
                <Button
                  onClick={handleManageBilling}
                  variant="outline"
                  className="w-full border-orange-300 hover:bg-orange-50"
                  title="Abrir portal de gerenciamento Stripe"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Gerenciar Método de Pagamento
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              )}

              {!isPremium && (
                <Button
                  onClick={() => navigate('/subscription')}
                  className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Fazer Upgrade para Premium
                </Button>
              )}
            </div>
          </Card>

          {/* Quick Stats */}
          <Card className="p-6 border-2 border-gray-200">
            <h3 className="text-lg font-semibold text-amber-900 mb-4">Benefícios {isPremium ? 'Ativos' : 'Premium'}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Check className={`w-5 h-5 mt-0.5 ${isPremium ? 'text-green-600' : 'text-gray-400'}`} />
                <span className={isPremium ? 'text-gray-700' : 'text-gray-500'}>Receitas ilimitadas</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className={`w-5 h-5 mt-0.5 ${isPremium ? 'text-green-600' : 'text-gray-400'}`} />
                <span className={isPremium ? 'text-gray-700' : 'text-gray-500'}>IA ilimitada</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className={`w-5 h-5 mt-0.5 ${isPremium ? 'text-green-600' : 'text-gray-400'}`} />
                <span className={isPremium ? 'text-gray-700' : 'text-gray-500'}>Famílias ilimitadas</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className={`w-5 h-5 mt-0.5 ${isPremium ? 'text-green-600' : 'text-gray-400'}`} />
                <span className={isPremium ? 'text-gray-700' : 'text-gray-500'}>Planejador semanal</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className={`w-5 h-5 mt-0.5 ${isPremium ? 'text-green-600' : 'text-gray-400'}`} />
                <span className={isPremium ? 'text-gray-700' : 'text-gray-500'}>Assistente de voz</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className={`w-5 h-5 mt-0.5 ${isPremium ? 'text-green-600' : 'text-gray-400'}`} />
                <span className={isPremium ? 'text-gray-700' : 'text-gray-500'}>Sem anúncios</span>
              </li>
            </ul>
          </Card>
        </div>

        {/* Payment History */}
        {paymentHistory.length > 0 && (
          <Card className="mt-6 p-6 border-2 border-gray-200">
            <h3 className="text-xl font-semibold text-amber-900 mb-4">Histórico de Pagamentos</h3>
            <div className="space-y-3">
              {paymentHistory.map((transaction, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      transaction.payment_status === 'paid' ? 'bg-green-100' : 'bg-gray-200'
                    }`}>
                      {transaction.payment_status === 'paid' ? (
                        <Check className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        Assinatura Premium - {transaction.plan}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(transaction.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">
                      {transaction.currency === 'usd' ? '$' : 'R$'} {transaction.amount.toFixed(2)}
                    </p>
                    <Badge variant={transaction.payment_status === 'paid' ? 'success' : 'secondary'}>
                      {transaction.payment_status === 'paid' ? 'Pago' : 'Pendente'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Assinatura Premium?</DialogTitle>
            <DialogDescription className="pt-2">
              Tem certeza que deseja cancelar sua assinatura Premium?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-900 font-medium mb-2">⚠️ O que acontecerá:</p>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• Você manterá acesso Premium até o fim do período pago</li>
                <li>• Após isso, voltará para o plano Gratuito</li>
                <li>• Limite de 5 receitas/mês e 3 IA/semana</li>
                <li>• Você pode reativar a qualquer momento</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              disabled={cancelling}
            >
              Manter Assinatura
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelando...' : 'Sim, Cancelar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageSubscriptionPage;
